import { Checkbox, classNames, Spinner, Table, toast } from '@cma/common'
import { useEffect, useState } from 'react'
import { useContent } from './ContentContext'
import { RenameInput } from './RenameInput'
import { getFileIcon } from './utils'
import { useCreatePlatformClientFolder } from './useCreatePlatformClientFolder'

export function GridFolderCreateInput() {
  const {
    movingFileIds,
    createNewFolder,
    setIsRenaming,
    setMovingFileIds,
    setCreateNewFolder
  } = useContent()

  const [isRenamingLocal, setIsRenamingLocal] = useState(true)
  const [localName, setLocalName] = useState('New Folder')

  const fileId = movingFileIds[0]
  const isSelected = createNewFolder && fileId

  const {
    mutate: createFolder,
    isLoading: isCreatingFolder,
    isSuccess: hasCreatedFolder
  } = useCreatePlatformClientFolder()

  useEffect(() => {
    if (isSelected) {
      setIsRenaming(true)
      setIsRenamingLocal(true)
    }
  }, [isSelected, setIsRenaming, setIsRenamingLocal])

  const AssetIcon = getFileIcon('Folder')

  if (hasCreatedFolder) {
    return null
  }

  return (
    <div className="relative min-h-full w-full">
      <div
        role="button"
        tabIndex={0}
        className={classNames(
          'relative flex aspect-square cursor-pointer flex-col items-center justify-center rounded-md px-3 py-4 transition focus:outline-none',
          {
            'bg-brand-white hover:bg-brand-white': isSelected,
            'bg-gray-50 hover:bg-gray-100': !isSelected,
            'animate-pulse cursor-default': isCreatingFolder
          }
        )}>
        <span>
          <AssetIcon style={{ height: 52, width: 68 }} />
        </span>
      </div>
      {isRenamingLocal ? (
        <>
          {isCreatingFolder ? (
            <div className="mt-2 flex min-h-[24px] items-center justify-center px-1 pb-1">
              <span className="helix-small line-clamp-2 block w-full break-words text-gray-400">
                {localName}
              </span>
              <div className="helix-text-brand h-3.5 w-3.5">
                <Spinner />
              </div>
            </div>
          ) : (
            <RenameInput
              defaultValue="New Folder"
              onSubmit={(value) => {
                if (!value.trim()) {
                  return false
                }

                setLocalName(value)

                createFolder(
                  {
                    input: {
                      name: value
                    }
                  },
                  {
                    onSuccess() {
                      toast(`${value} folder created successfully`, {
                        variant: 'success'
                      })
                    },
                    onError(error) {
                      toast(error.message, {
                        variant: 'error'
                      })
                    },
                    onSettled() {
                      setIsRenamingLocal(false)
                      setIsRenaming(false)
                      setCreateNewFolder(false)
                      setMovingFileIds([])
                    }
                  }
                )

                return true
              }}
              onCancel={() => {
                setIsRenamingLocal(false)
                setIsRenaming(false)
                setCreateNewFolder(false)
              }}
            />
          )}
        </>
      ) : (
        <div className="mt-2 px-1">
          <span
            className="helix-small block w-full break-words text-gray-600"
            style={{
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              overflow: 'hidden'
            }}>
            {localName}
          </span>
        </div>
      )}
    </div>
  )
}

export function TableFolderCreateInput() {
  const {
    setIsRenaming,
    movingFileIds,
    createNewFolder,
    setMovingFileIds,
    setCreateNewFolder
  } = useContent()

  const [isRenamingLocal, setIsRenamingLocal] = useState(true)
  const [localName, setLocalName] = useState('New Folder')

  const fileId = movingFileIds[0]
  const isSelected = createNewFolder && fileId

  const {
    mutate: createFolder,
    isLoading: isCreatingFolder,
    isSuccess: hasCreatedFolder
  } = useCreatePlatformClientFolder()

  useEffect(() => {
    if (isSelected) {
      setIsRenaming(true)
      setIsRenamingLocal(true)
    }
  }, [isSelected, setIsRenaming, setIsRenamingLocal])

  const AssetIcon = getFileIcon('Folder')

  if (hasCreatedFolder) {
    return null
  }

  return (
    <>
      <Table.Data padding="compact" align="center">
        <div
          className={classNames('flex items-center justify-center px-4', {
            'animate-pulse': isCreatingFolder
          })}>
          <Checkbox disabled />
        </div>
      </Table.Data>
      <Table.Data padding="compact">
        <div
          className={classNames(
            'flex min-h-[52px] w-full max-w-[260px] items-center space-x-3',
            {
              'animate-pulse': isCreatingFolder
            }
          )}>
          <div className="flex items-center">
            <AssetIcon className="h-[41px] w-[32px]" />
          </div>
          <div className="helix-body ml-1 flex flex-1 items-center overflow-hidden py-1 text-gray-900">
            {isRenamingLocal ? (
              <>
                {isCreatingFolder ? (
                  <div className="mt-2 flex min-h-[24px] items-center justify-center gap-2 px-1 pb-1">
                    <span className="helix-body line-clamp-2 block w-full break-all text-gray-400">
                      {localName}
                    </span>
                    <div className="helix-text-brand h-4 w-4">
                      <Spinner />
                    </div>
                  </div>
                ) : (
                  <RenameInput
                    defaultValue={localName}
                    saveButtonPosition="inline"
                    onSubmit={(value) => {
                      if (!value.trim()) {
                        return false
                      }

                      setLocalName(value)

                      createFolder(
                        {
                          input: {
                            name: value
                          }
                        },
                        {
                          onSuccess() {
                            toast(`${value} folder created successfully`, {
                              variant: 'success'
                            })
                          },
                          onError(error) {
                            toast(error.message, {
                              variant: 'error'
                            })
                          },
                          onSettled() {
                            setIsRenamingLocal(false)
                            setIsRenaming(false)
                            setCreateNewFolder(false)
                            setMovingFileIds([])
                          }
                        }
                      )

                      return true
                    }}
                    onCancel={() => {
                      setIsRenamingLocal(false)
                      setIsRenaming(false)
                      setCreateNewFolder(false)
                    }}
                  />
                )}
              </>
            ) : (
              <span>{localName}</span>
            )}
          </div>
        </div>
      </Table.Data>
    </>
  )
}
